import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { adminPlayersAPI, adminCantonsAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { SKILL_RANGE_OPTIONS, exactLevelsForRange, cantonOptionLabel } from '../constants/playerSignup';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB' };
const KIT_SIZE_OPTIONS = ['XXL', 'XL', 'L', 'M', 'S'];

const Wrap = styled.div`
  padding: 0.25rem 0 1.5rem;
`;

const Intro = styled.div`
  margin-bottom: 1.25rem;
  h3 {
    margin: 0 0 0.35rem;
    color: ${BRAND.blue};
    font-size: 1.35rem;
  }
  p {
    margin: 0;
    color: #6b7280;
    font-size: 0.9rem;
    max-width: 52rem;
    line-height: 1.45;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 360px;
  padding: 0.55rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 2px rgba(4, 54, 99, 0.12);
  }
`;

const Select = styled.select`
  padding: 0.55rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
  }
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0.3rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem 1rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FormFull = styled.div`
  grid-column: 1 / -1;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
`;

const Btn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? BRAND.blue : '#d1d5db')};
  background: ${(p) => (p.$primary ? BRAND.blue : 'white')};
  color: ${(p) => (p.$primary ? '#fff' : '#374151')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    filter: brightness(0.97);
  }
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th {
    text-align: left;
    padding: 0.75rem 1rem;
    font-weight: 600;
    color: #374151;
    font-size: 0.8125rem;
    border-bottom: 2px solid #e5e7eb;
    background: #f9fafb;
  }
  td {
    padding: 0.7rem 1rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
    color: #1f2937;
  }
  tbody tr {
    cursor: pointer;
    &:hover td {
      background: #f8fafc;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.8125rem;
  color: #6b7280;
`;

const PageBtn = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  font-size: 0.8125rem;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    border-color: ${BRAND.blue};
    color: ${BRAND.blue};
  }
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  &:hover {
    border-color: ${BRAND.blue};
    color: ${BRAND.blue};
  }
`;

const DetailHeader = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  h2 {
    margin: 0 0 0.35rem;
    color: ${BRAND.blue};
    font-size: 1.5rem;
  }
  .meta {
    color: #6b7280;
    font-size: 0.875rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.25rem;
  }
  .note {
    margin-top: 0.75rem;
    padding: 0.65rem 0.85rem;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    font-size: 0.8125rem;
    color: #9a3412;
    line-height: 1.4;
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Kpi = styled.div`
  background: ${(p) => p.$accent || `linear-gradient(135deg, ${BRAND.blue} 0%, #032a4a 100%)`};
  color: white;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  .n {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }
  .l {
    font-size: 0.7rem;
    opacity: 0.9;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-top: 0.2rem;
  }
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.15rem 1.35rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  h4 {
    margin: 0 0 0.75rem;
    color: #111827;
    font-size: 1rem;
  }
  .sub {
    margin: -0.35rem 0 0.85rem;
    font-size: 0.8rem;
    color: #6b7280;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
  span:first-child {
    color: #6b7280;
  }
  span:last-child {
    font-weight: 600;
    color: #111827;
  }
`;

const TrendBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  min-height: 100px;
  padding-top: 0.5rem;
`;

const TrendBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  .bar {
    width: 100%;
    max-width: 48px;
    background: ${BRAND.blue};
    border-radius: 4px 4px 0 0;
    min-height: 4px;
  }
  .lbl {
    font-size: 0.65rem;
    color: #6b7280;
    text-align: center;
    line-height: 1.2;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const Deferred = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: #9ca3af;
  font-style: italic;
`;

const Empty = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.35rem 1.5rem;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  h3 {
    margin: 0 0 0.5rem;
    color: ${BRAND.blue};
    font-size: 1.15rem;
  }
  p {
    margin: 0 0 1rem;
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.45;
  }
`;

function getRangeForLevel(level) {
  if (level == null || level === '') return '';
  const n = parseFloat(level);
  if (Number.isNaN(n)) return '';
  for (const r of SKILL_RANGE_OPTIONS) {
    const [min, max] = r.value.split('-').map(Number);
    if (n >= min && n <= max) return r.value;
  }
  return '';
}

function displayName(p) {
  return [p?.first_name, p?.last_name].filter(Boolean).join(' ') || p?.email || 'Player';
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n)}%`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return '—';
  }
}

function toDateInput(d) {
  if (!d) return '';
  try {
    const s = typeof d === 'string' ? d : new Date(d).toISOString();
    return s.slice(0, 10);
  } catch {
    return '';
  }
}

function swissLicensedToForm(v) {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function profileToForm(p) {
  if (!p) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      sex: '',
      homeClub: '',
      homeCanton: '',
      skillLevel: '',
      skillRange: '',
      instagramHandle: '',
      uiLocale: '',
      streetAddress: '',
      postalCode: '',
      city: '',
      country: '',
      nationalityPrimary: '',
      nationalitySecondary: '',
      dominantHand: '',
      preferredCourtSide: '',
      swissTennisPadelLicensed: '',
      kitSize: '',
      kitName: '',
      profilePublic: false,
      partnerCommunications: false,
      profilePictureUrl: '',
    };
  }
  const skill = p.skill_level != null ? Number(p.skill_level) : null;
  return {
    firstName: p.first_name || '',
    lastName: p.last_name || '',
    email: p.email || '',
    phoneNumber: p.phone_number || '',
    dateOfBirth: toDateInput(p.date_of_birth),
    sex: p.sex || '',
    homeClub: p.home_club || '',
    homeCanton: p.home_canton || '',
    skillLevel: skill != null && !Number.isNaN(skill) ? skill.toFixed(1) : '',
    skillRange: getRangeForLevel(skill),
    instagramHandle: p.instagram_handle || '',
    uiLocale: p.ui_locale || '',
    streetAddress: p.street_address || '',
    postalCode: p.postal_code || '',
    city: p.city || '',
    country: p.country || '',
    nationalityPrimary: p.nationality_primary || '',
    nationalitySecondary: p.nationality_secondary || '',
    dominantHand: p.dominant_hand || '',
    preferredCourtSide: p.preferred_court_side || '',
    swissTennisPadelLicensed: swissLicensedToForm(p.swiss_tennis_padel_licensed),
    kitSize: p.t_shirt_size || p.short_size || '',
    kitName: p.kit_name || p.kitName || '',
    profilePublic: p.profile_public === true,
    partnerCommunications: p.partner_communications === true,
    profilePictureUrl: p.profile_picture_url || '',
  };
}

function StatBlock({ title, sub, stats }) {
  if (!stats) return null;
  const rows = [
    ['Matches', stats.totalMatches],
    ['Wins', stats.wins],
    ['Losses', stats.losses],
    ['Win %', fmtPct(stats.winPercentage)],
    ['Sets W–L', `${stats.setsWon}–${stats.setsLost}`],
    ['Games W–L', `${stats.gamesWon}–${stats.gamesLost}`],
    ['Avg games won', stats.avgGamesWonPerMatch],
    ['Avg games lost', stats.avgGamesLostPerMatch],
    ['Completion rate', fmtPct(stats.matchCompletionRate)],
    ['Winning streak', stats.winningStreak],
    ['Longest win streak', stats.longestWinningStreak],
    ['Losing streak', stats.losingStreak],
  ];
  return (
    <SectionCard>
      <h4>{title}</h4>
      {sub ? <p className="sub">{sub}</p> : null}
      {rows.map(([k, v]) => (
        <StatRow key={k}>
          <span>{k}</span>
          <span>{v == null || v === '' ? '—' : v}</span>
        </StatRow>
      ))}
    </SectionCard>
  );
}

function PlayerDetail({ playerId, onBack, canEdit, isSuperAdmin }) {
  const queryClient = useQueryClient();
  const [histType, setHistType] = useState('all');
  const [histResult, setHistResult] = useState('all');
  const [histOffset, setHistOffset] = useState(0);
  const [form, setForm] = useState(() => profileToForm(null));
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const histLimit = 25;

  useEffect(() => {
    setHistOffset(0);
  }, [histType, histResult, playerId]);

  const {
    data: profileRes,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery(['admin-player-profile', playerId], () => adminPlayersAPI.getById(playerId), {
    enabled: !!playerId,
  });

  const { data: statsRes, isLoading: statsLoading, error: statsError } = useQuery(
    ['admin-player-stats', playerId, histType, histResult, histOffset],
    () =>
      adminPlayersAPI.getStats(playerId, {
        type: histType,
        result: histResult,
        limit: histLimit,
        offset: histOffset,
      }),
    { enabled: !!playerId, keepPreviousData: true }
  );

  const { data: cantonsRes } = useQuery(['admin-cantons'], () => adminCantonsAPI.getList(), {
    staleTime: 60 * 60 * 1000,
  });

  const profile = profileRes?.data?.profile;
  const stats = statsRes?.data;
  const cantonOptions = useMemo(() => {
    const raw = cantonsRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [cantonsRes]);

  useEffect(() => {
    if (profile) setForm(profileToForm(profile));
  }, [profile, playerId]);

  const trendMax = useMemo(() => {
    const vals = (stats?.trend || []).map((t) => Number(t.winPercentage) || 0);
    return Math.max(1, ...vals, 1);
  }, [stats?.trend]);

  const exactOpts = exactLevelsForRange(form.skillRange);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveProfile = async () => {
    if (!canEdit) return;
    const email = String(form.email || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('A valid email is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        email,
        phoneNumber: form.phoneNumber.trim() || null,
        dateOfBirth: form.dateOfBirth || null,
        sex: form.sex || null,
        homeClub: form.homeClub.trim() || null,
        homeCanton: form.homeCanton || null,
        instagramHandle: form.instagramHandle.trim() || null,
        uiLocale: form.uiLocale || null,
        streetAddress: form.streetAddress.trim() || null,
        postalCode: form.postalCode.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        nationalityPrimary: form.nationalityPrimary.trim() || null,
        nationalitySecondary: form.nationalitySecondary.trim() || null,
        dominantHand: form.dominantHand || null,
        preferredCourtSide: form.preferredCourtSide || null,
        swissTennisPadelLicensed:
          form.swissTennisPadelLicensed === 'yes'
            ? true
            : form.swissTennisPadelLicensed === 'no'
              ? false
              : null,
        tShirtSize: form.kitSize || null,
        shortSize: form.kitSize || null,
        kitName: form.kitName.trim() || null,
        profilePublic: form.profilePublic === true,
        partnerCommunications: form.partnerCommunications === true,
        profilePictureUrl: form.profilePictureUrl.trim() || null,
      };
      if (form.skillLevel) {
        const nextSkill = Number(form.skillLevel);
        const prevSkill =
          profile.skill_level != null ? Number(profile.skill_level) : null;
        if (
          Number.isFinite(nextSkill) &&
          (prevSkill == null || Math.abs(nextSkill - prevSkill) > 0.001)
        ) {
          payload.skillLevel = nextSkill;
        }
      }
      const res = await adminPlayersAPI.update(playerId, payload);
      toast.success(res?.message || 'Profile saved');
      queryClient.invalidateQueries(['admin-player-profile', playerId]);
      queryClient.invalidateQueries(['admin-player-stats', playerId]);
      queryClient.invalidateQueries(['admin-players']);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async () => {
    if (!canEdit) return;
    setResetSending(true);
    try {
      const body =
        useCustomEmail && customEmail.trim()
          ? { email: customEmail.trim() }
          : {};
      const res = await adminPlayersAPI.sendPasswordReset(playerId, body);
      toast.success(res?.message || `Reset link sent to ${res?.deliveredTo || 'email'}`);
      setResetOpen(false);
      setUseCustomEmail(false);
      setCustomEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to send reset link');
    } finally {
      setResetSending(false);
    }
  };

  const viewAsPlayer = async () => {
    if (!isSuperAdmin) return;
    setImpersonating(true);
    try {
      const res = await adminPlayersAPI.impersonate(playerId);
      const token = res?.data?.token || res?.token;
      const redirectPath = res?.data?.redirectPath || res?.redirectPath || '/player/tournaments';
      if (!token) {
        toast.error('Support session did not return a token');
        return;
      }
      const url = `${window.location.origin}/player/support-session#token=${encodeURIComponent(token)}&next=${encodeURIComponent(redirectPath)}`;
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Pop-up blocked — allow pop-ups for this site, then try again');
        return;
      }
      toast.success('Opened player support session in a new tab (30 min)');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to start support session');
    } finally {
      setImpersonating(false);
    }
  };

  if ((profileLoading && !profile) || (statsLoading && !stats)) {
    return <Empty>Loading player…</Empty>;
  }
  if (profileError) {
    return (
      <Empty style={{ color: BRAND.red }}>
        {profileError?.response?.data?.message || profileError?.message || 'Failed to load player'}
      </Empty>
    );
  }
  if (!profile) {
    return <Empty>Player not found.</Empty>;
  }

  const hist = stats?.matchHistory || { items: [], total: 0 };

  return (
    <div>
      <BackBtn type="button" onClick={onBack}>
        ← Back to players
      </BackBtn>

      <DetailHeader>
        <h2>{displayName(profile)}</h2>
        <div className="meta">
          <span>{profile.email}</span>
          <span>{profile.phone_number || 'No phone'}</span>
          <span>Skill: {profile.skill_level != null ? profile.skill_level : '—'}</span>
          <span>
            One-time self-change:{' '}
            {profile.allow_one_skill_change === true ? 'available' : 'used / not granted'}
          </span>
          <span>Registered: {fmtDate(profile.created_at)}</span>
          <span>Kit name: {profile.kit_name || profile.kitName || '—'}</span>
        </div>
        {stats?.attributionNote ? <div className="note">{stats.attributionNote}</div> : null}
      </DetailHeader>

      <SectionCard>
        <h4>Profile</h4>
        <p className="sub">
          Full editable account fields (no password). Saving skill clears the one-time self-change
          flag. Admin can change canton and skill even when the player cannot.
        </p>
        <FormGrid>
          <div>
            <FormLabel>First name</FormLabel>
            <TextInput
              value={form.firstName}
              onChange={(e) => setField('firstName', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Last name</FormLabel>
            <TextInput
              value={form.lastName}
              onChange={(e) => setField('lastName', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Email (login)</FormLabel>
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Phone</FormLabel>
            <TextInput
              value={form.phoneNumber}
              onChange={(e) => setField('phoneNumber', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Date of birth</FormLabel>
            <TextInput
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => setField('dateOfBirth', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Sex</FormLabel>
            <Select
              value={form.sex}
              onChange={(e) => setField('sex', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
          </div>
          <div>
            <FormLabel>Home club</FormLabel>
            <TextInput
              value={form.homeClub}
              onChange={(e) => setField('homeClub', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Home canton</FormLabel>
            <Select
              value={form.homeCanton}
              onChange={(e) => setField('homeCanton', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">—</option>
              {cantonOptions.map((c) => (
                <option key={c.code || c.id} value={c.code}>
                  {c.name || cantonOptionLabel(c) || c.code}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FormLabel>Skill range</FormLabel>
            <Select
              value={form.skillRange}
              onChange={(e) => {
                setField('skillRange', e.target.value);
                setField('skillLevel', '');
              }}
              disabled={!canEdit}
            >
              <option value="">—</option>
              {SKILL_RANGE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FormLabel>Exact skill</FormLabel>
            <Select
              value={form.skillLevel}
              onChange={(e) => setField('skillLevel', e.target.value)}
              disabled={!canEdit || !form.skillRange}
            >
              <option value="">—</option>
              {exactOpts.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FormLabel>Dominant hand</FormLabel>
            <Select
              value={form.dominantHand}
              onChange={(e) => setField('dominantHand', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">—</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </Select>
          </div>
          <div>
            <FormLabel>Preferred court side</FormLabel>
            <Select
              value={form.preferredCourtSide}
              onChange={(e) => setField('preferredCourtSide', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">—</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="both">Both</option>
            </Select>
          </div>
          <div>
            <FormLabel>Swiss Tennis Padel licensed</FormLabel>
            <Select
              value={form.swissTennisPadelLicensed}
              onChange={(e) => setField('swissTennisPadelLicensed', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </div>
          <div>
            <FormLabel>UI language</FormLabel>
            <Select
              value={form.uiLocale}
              onChange={(e) => setField('uiLocale', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">Unset</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </Select>
          </div>
          <div>
            <FormLabel>Instagram</FormLabel>
            <TextInput
              value={form.instagramHandle}
              onChange={(e) => setField('instagramHandle', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Profile picture URL</FormLabel>
            <TextInput
              value={form.profilePictureUrl}
              onChange={(e) => setField('profilePictureUrl', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Kit size</FormLabel>
            <Select
              value={form.kitSize}
              onChange={(e) => setField('kitSize', e.target.value)}
              disabled={!canEdit}
            >
              <option value="">—</option>
              {KIT_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FormLabel>Kit name</FormLabel>
            <TextInput
              value={form.kitName}
              onChange={(e) => setField('kitName', String(e.target.value).slice(0, 12))}
              disabled={!canEdit}
              maxLength={12}
              placeholder="Max 12 characters"
            />
          </div>
          <FormFull>
            <FormLabel>Street address</FormLabel>
            <TextInput
              value={form.streetAddress}
              onChange={(e) => setField('streetAddress', e.target.value)}
              disabled={!canEdit}
            />
          </FormFull>
          <div>
            <FormLabel>Postal code</FormLabel>
            <TextInput
              value={form.postalCode}
              onChange={(e) => setField('postalCode', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>City</FormLabel>
            <TextInput
              value={form.city}
              onChange={(e) => setField('city', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Country</FormLabel>
            <TextInput
              value={form.country}
              onChange={(e) => setField('country', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Nationality (primary)</FormLabel>
            <TextInput
              value={form.nationalityPrimary}
              onChange={(e) => setField('nationalityPrimary', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <FormLabel>Nationality (secondary)</FormLabel>
            <TextInput
              value={form.nationalitySecondary}
              onChange={(e) => setField('nationalitySecondary', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <FormFull>
            <CheckRow>
              <input
                type="checkbox"
                checked={form.profilePublic}
                onChange={(e) => setField('profilePublic', e.target.checked)}
                disabled={!canEdit}
              />
              Profile listed publicly to other players
            </CheckRow>
          </FormFull>
          <FormFull>
            <CheckRow>
              <input
                type="checkbox"
                checked={form.partnerCommunications}
                onChange={(e) => setField('partnerCommunications', e.target.checked)}
                disabled={!canEdit}
              />
              Partner communications opt-in
            </CheckRow>
          </FormFull>
        </FormGrid>
        <BtnRow>
          {canEdit ? (
            <>
              <Btn type="button" $primary onClick={saveProfile} disabled={saving}>
                {saving ? 'Saving…' : 'Save profile'}
              </Btn>
              <Btn
                type="button"
                onClick={() => {
                  setUseCustomEmail(false);
                  setCustomEmail('');
                  setResetOpen(true);
                }}
              >
                Send password reset link
              </Btn>
            </>
          ) : (
            <Deferred>You have view-only access — Save and password reset are disabled.</Deferred>
          )}
          {isSuperAdmin ? (
            <Btn type="button" onClick={viewAsPlayer} disabled={impersonating}>
              {impersonating ? 'Opening…' : 'View as player'}
            </Btn>
          ) : null}
        </BtnRow>
      </SectionCard>

      {statsError ? (
        <Empty style={{ color: BRAND.red }}>
          {statsError?.response?.data?.message || statsError?.message || 'Failed to load stats'}
        </Empty>
      ) : !stats ? (
        <Empty>Loading career statistics…</Empty>
      ) : (
        <>
          <KpiGrid>
            <Kpi>
              <div className="n">{stats.currentLevel ?? '—'}</div>
              <div className="l">Current level</div>
            </Kpi>
            <Kpi $accent="linear-gradient(135deg, #0a4a7a 0%, #043663 100%)">
              <div className="n">{stats.highestLevelReached ?? '—'}</div>
              <div className="l">Highest (est.)</div>
            </Kpi>
            <Kpi>
              <div className="n">{stats.seasonsPlayed ?? 0}</div>
              <div className="l">Phases played</div>
            </Kpi>
            <Kpi $accent={`linear-gradient(135deg, ${BRAND.red} 0%, #b52217 100%)`}>
              <div className="n">
                {(stats.leagueViaTeams?.wins ?? 0) + (stats.individual?.wins ?? 0)}–
                {(stats.leagueViaTeams?.losses ?? 0) + (stats.individual?.losses ?? 0)}
              </div>
              <div className="l">Combined W–L</div>
            </Kpi>
            <Kpi>
              <div className="n">{stats.leagueViaTeams?.winningStreak ?? 0}</div>
              <div className="l">League win streak</div>
            </Kpi>
            <Kpi>
              <div className="n">{stats.individual?.winningStreak ?? 0}</div>
              <div className="l">Find Match streak</div>
            </Kpi>
          </KpiGrid>

          <TwoCol>
            <StatBlock
              title="League (via teams)"
              sub={stats.leagueViaTeams?.note}
              stats={stats.leagueViaTeams}
            />
            <StatBlock title="Find Match (personal)" stats={stats.individual} />
          </TwoCol>

          <SectionCard>
            <h4>Career</h4>
            <StatRow>
              <span>Promotions</span>
              <span>{stats.career?.promotionsCount ?? 0}</span>
            </StatRow>
            <StatRow>
              <span>Relegations</span>
              <span>{stats.career?.relegationsCount ?? 0}</span>
            </StatRow>
            <StatRow>
              <span>Forfeit phases</span>
              <span>{stats.career?.forfeitSeasonsCount ?? 0}</span>
            </StatRow>
            {(stats.career?.promotions || []).slice(0, 5).map((p) => (
              <StatRow key={p.id}>
                <span>
                  Promote · {p.team_name} · {p.completed_season_name || 'phase'}
                </span>
                <span>
                  {p.old_division_name || '—'} → {p.new_division_name || '—'}
                </span>
              </StatRow>
            ))}
            {(stats.career?.relegations || []).slice(0, 5).map((p) => (
              <StatRow key={p.id}>
                <span>
                  Relegate · {p.team_name} · {p.completed_season_name || 'phase'}
                </span>
                <span>
                  {p.old_division_name || '—'} → {p.new_division_name || '—'}
                </span>
              </StatRow>
            ))}
            {(stats.career?.divisionWinnerProxies || []).length > 0 ? (
              <>
                <p className="sub" style={{ marginTop: '0.75rem' }}>
                  Division winner proxy (team finished #1)
                </p>
                {stats.career.divisionWinnerProxies.map((t) => (
                  <StatRow key={`${t.seasonId}-${t.teamId}`}>
                    <span>
                      {t.seasonName} · {t.teamName}
                    </span>
                    <span>#1</span>
                  </StatRow>
                ))}
              </>
            ) : null}
            <Deferred style={{ marginTop: '0.75rem' }}>
              {stats.deferred?.awards} {stats.deferred?.finals}
            </Deferred>
          </SectionCard>

          <TwoCol>
            <SectionCard>
              <h4>Roster teammates</h4>
              <p className="sub">Players who share an active team roster</p>
              {(stats.people?.rosterTeammates || []).length === 0 ? (
                <Deferred>No roster teammates found.</Deferred>
              ) : (
                (stats.people.rosterTeammates || []).slice(0, 15).map((t) => (
                  <StatRow key={t.id}>
                    <span>
                      {[t.first_name, t.last_name].filter(Boolean).join(' ') || t.email}
                    </span>
                    <span>{t.shared_teams} team(s)</span>
                  </StatRow>
                ))
              )}
            </SectionCard>
            <SectionCard>
              <h4>Find Match partners / opponents</h4>
              <p className="sub">From completed individual matches</p>
              {(stats.people?.findMatch || []).length === 0 ? (
                <Deferred>No Find Match history.</Deferred>
              ) : (
                (stats.people.findMatch || []).slice(0, 15).map((t) => (
                  <StatRow key={t.id}>
                    <span>{[t.firstName, t.lastName].filter(Boolean).join(' ')}</span>
                    <span>
                      T {t.asTeammate} · Opp {t.asOpponent}
                    </span>
                  </StatRow>
                ))
              )}
            </SectionCard>
          </TwoCol>

          <SectionCard>
            <h4>Phase-by-phase (league via teams)</h4>
            {(stats.bySeason || []).length === 0 ? (
              <Deferred>No phase standings linked via roster.</Deferred>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Phase</th>
                      <th>Team</th>
                      <th>W–L</th>
                      <th>Win %</th>
                      <th>Sets</th>
                      <th>Matches</th>
                      <th>Pts</th>
                      <th>Best pos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.bySeason.map((r) => (
                      <tr key={`${r.seasonId}-${r.teamId}`} style={{ cursor: 'default' }}>
                        <td>{r.seasonName}</td>
                        <td>{r.teamName}</td>
                        <td>
                          {r.wins}–{r.losses}
                        </td>
                        <td>{fmtPct(r.winPercentage)}</td>
                        <td>
                          {r.setsWon}–{r.setsLost}
                        </td>
                        <td>{r.matchesPlayed}</td>
                        <td>{r.points}</td>
                        <td>{r.bestPosition ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </SectionCard>

          <SectionCard>
            <h4>Performance trend</h4>
            <p className="sub">League win % by phase (team-attributed)</p>
            {(stats.trend || []).length === 0 ? (
              <Deferred>Not enough phase data for a trend.</Deferred>
            ) : (
              <TrendBars>
                {stats.trend.map((t) => (
                  <TrendBar key={t.seasonId}>
                    <div
                      className="bar"
                      style={{
                        height: `${Math.max(8, (Number(t.winPercentage) / trendMax) * 90)}px`,
                      }}
                      title={`${t.seasonName}: ${fmtPct(t.winPercentage)}`}
                    />
                    <div className="lbl">
                      {fmtPct(t.winPercentage)}
                      <br />
                      {(t.seasonName || '').slice(0, 12)}
                    </div>
                  </TrendBar>
                ))}
              </TrendBars>
            )}
          </SectionCard>

          <SectionCard>
            <h4>Match history</h4>
            <Filters>
              <Select value={histType} onChange={(e) => setHistType(e.target.value)}>
                <option value="all">All types</option>
                <option value="league">League</option>
                <option value="individual">Find Match</option>
              </Select>
              <Select value={histResult} onChange={(e) => setHistResult(e.target.value)}>
                <option value="all">All results</option>
                <option value="win">Wins</option>
                <option value="loss">Losses</option>
              </Select>
            </Filters>
            {(hist.items || []).length === 0 ? (
              <Deferred>No matches for these filters.</Deferred>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Context</th>
                        <th>Opponent</th>
                        <th>Result</th>
                        <th>Sets</th>
                        <th>Games</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hist.items.map((m) => (
                        <tr key={`${m.type}-${m.id}`} style={{ cursor: 'default' }}>
                          <td>{fmtDate(m.date)}</td>
                          <td>{m.type === 'league' ? 'League' : 'Find Match'}</td>
                          <td>{m.seasonName || m.myLabel || '—'}</td>
                          <td>{m.opponentLabel || '—'}</td>
                          <td style={{ textTransform: 'capitalize' }}>{m.result}</td>
                          <td>
                            {m.setsWon}–{m.setsLost}
                          </td>
                          <td>{m.gamesWon != null ? `${m.gamesWon}–${m.gamesLost}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Pagination>
                  <span>
                    Showing {histOffset + 1}–{Math.min(histOffset + histLimit, hist.total)} of{' '}
                    {hist.total}
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <PageBtn
                      type="button"
                      disabled={histOffset <= 0}
                      onClick={() => setHistOffset(Math.max(0, histOffset - histLimit))}
                    >
                      Previous
                    </PageBtn>
                    <PageBtn
                      type="button"
                      disabled={histOffset + histLimit >= hist.total}
                      onClick={() => setHistOffset(histOffset + histLimit)}
                    >
                      Next
                    </PageBtn>
                  </div>
                </Pagination>
              </>
            )}
          </SectionCard>

          <SectionCard>
            <h4>Teams</h4>
            {(stats.teams || []).length === 0 ? (
              <Deferred>Not on any active team roster.</Deferred>
            ) : (
              (stats.teams || []).map((t) => (
                <StatRow key={t.id}>
                  <span>
                    {t.team_name}
                    {t.is_captain ? ' (captain)' : ''}
                  </span>
                  <span>
                    Skill {t.team_skill_level ?? '—'} · {t.canton || '—'}
                  </span>
                </StatRow>
              ))
            )}
          </SectionCard>
        </>
      )}

      {resetOpen ? (
        <ModalOverlay
          onClick={() => {
            if (!resetSending) setResetOpen(false);
          }}
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <h3>Send password reset link</h3>
            <p>
              Sends a link to <code>/reset-password</code>. A custom delivery address does not change
              the player’s login email.
            </p>
            <FormLabel>Deliver to</FormLabel>
            <TextInput
              value={useCustomEmail ? customEmail : profile.email || ''}
              onChange={(e) => setCustomEmail(e.target.value)}
              disabled={!useCustomEmail || resetSending}
              placeholder="email@example.com"
              style={{ marginBottom: '0.75rem' }}
            />
            <CheckRow style={{ marginBottom: '1rem' }}>
              <input
                type="checkbox"
                checked={useCustomEmail}
                onChange={(e) => {
                  setUseCustomEmail(e.target.checked);
                  if (!e.target.checked) setCustomEmail('');
                }}
                disabled={resetSending}
              />
              Send to a different email
            </CheckRow>
            <BtnRow style={{ marginTop: 0 }}>
              <Btn type="button" $primary onClick={sendReset} disabled={resetSending}>
                {resetSending ? 'Sending…' : 'Send link'}
              </Btn>
              <Btn type="button" onClick={() => setResetOpen(false)} disabled={resetSending}>
                Cancel
              </Btn>
            </BtnRow>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </div>
  );
}

export default function AdminPlayersSection() {
  const { adminUser } = useAdminAuth();
  const isSuperAdmin =
    adminUser?.isSuperAdmin === true ||
    adminUser?.is_super_admin === true ||
    adminUser?.role === 'super_admin';
  const canEdit =
    isSuperAdmin ||
    adminUser?.permissions?.teams?.edit === true;

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState('name');
  const [membership, setMembership] = useState('all');
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const showSeasonPhase = membership === 'league';
  const seasonPhase = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: showSeasonPhase,
    enabled: !selectedPlayerId && showSeasonPhase,
  });

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [qDebounced, sort, pageSize, membership, seasonPhase.campaignId, seasonPhase.seasonId]);

  const { data, isLoading, error } = useQuery(
    [
      'admin-players',
      qDebounced,
      page,
      pageSize,
      sort,
      membership,
      seasonPhase.campaignId,
      seasonPhase.seasonId,
    ],
    () =>
      adminPlayersAPI.getList({
        q: qDebounced || undefined,
        page,
        pageSize,
        sort,
        membership,
        ...(membership === 'league'
          ? {
              campaignId: seasonPhase.campaignId || undefined,
              seasonId: seasonPhase.seasonId || undefined,
            }
          : {}),
      }),
    { enabled: !selectedPlayerId, keepPreviousData: true }
  );

  const players = data?.data || [];
  const pagination = data?.pagination || { page: 1, pageSize, total: 0, totalPages: 1 };

  if (selectedPlayerId) {
    return (
      <Wrap>
        <PlayerDetail
          playerId={selectedPlayerId}
          onBack={() => setSelectedPlayerId(null)}
          canEdit={canEdit}
          isSuperAdmin={isSuperAdmin}
        />
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Intro>
        <h3>Players</h3>
        <p>
          Searchable directory of players in this country tenant. Use All / In a team / No team, then
          for league players filter by Season and Phase (team registration). Open a player to edit
          profile, send a password-reset link, or review career statistics.
        </p>
      </Intro>

      <Toolbar>
        <SearchInput
          type="search"
          placeholder="Search name, email, phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select value={membership} onChange={(e) => setMembership(e.target.value)}>
          <option value="all">All players</option>
          <option value="league">In a team (league)</option>
          <option value="account">No team (account only)</option>
        </Select>
        {showSeasonPhase ? (
          <AdminSeasonPhaseFilters
            campaignId={seasonPhase.campaignId}
            seasonId={seasonPhase.seasonId}
            seasonOptions={seasonPhase.seasonOptions}
            phaseOptions={seasonPhase.phaseOptions}
            onCampaignChange={seasonPhase.onCampaignChange}
            onPhaseChange={seasonPhase.onPhaseChange}
            allowAll
            hideLabels
            allSeasonsLabel="All seasons"
            allPhasesLabel="All phases"
          />
        ) : null}
        <Select value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="name">Sort: Name</option>
          <option value="created_at">Sort: Newest</option>
          <option value="created_at_asc">Sort: Oldest</option>
          <option value="skill_desc">Sort: Skill high→low</option>
          <option value="skill_asc">Sort: Skill low→high</option>
        </Select>
        <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </Select>
      </Toolbar>

      <Card>
        {isLoading ? (
          <Empty>Loading players…</Empty>
        ) : error ? (
          <Empty style={{ color: BRAND.red }}>
            {error?.response?.data?.message || error?.message || 'Failed to load players'}
          </Empty>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Skill</th>
                    <th>Teams</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {players.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ padding: '1.5rem', color: '#6b7280', cursor: 'default' }}
                      >
                        No players found.
                      </td>
                    </tr>
                  ) : (
                    players.map((p) => (
                      <tr key={p.id} onClick={() => setSelectedPlayerId(p.id)}>
                        <td>{displayName(p)}</td>
                        <td>{p.email}</td>
                        <td>{p.phone_number || '—'}</td>
                        <td>{p.skill_level != null ? p.skill_level : '—'}</td>
                        <td>{p.teams_count ?? 0}</td>
                        <td>{fmtDate(p.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
            <Pagination>
              <span>
                Page {pagination.page} of {pagination.totalPages} · {pagination.total} players
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <PageBtn
                  type="button"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </PageBtn>
                <PageBtn
                  type="button"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </PageBtn>
              </div>
            </Pagination>
          </>
        )}
      </Card>
    </Wrap>
  );
}
